import React from "react";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import color from "../../../Constants/colors";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";

export const voicesToolbarSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 1.5,
  flexWrap: "wrap",
  mb: 2.5,
};

export interface VoicesSegment<T extends string> {
  value: T;
  label: string;
  count: number;
  tone?: string;
}

interface SegmentedProps<T extends string> {
  segments: VoicesSegment<T>[];
  value: T;
  onChange: (value: T) => void;
}

export const VoicesSegmented = <T extends string>({
  segments,
  value,
  onChange,
}: SegmentedProps<T>) => {
  const { colors: workspace } = useWorkspaceColors();

  return (
    <Box
      role="radiogroup"
      sx={{
        display: "inline-flex",
        flexWrap: "wrap",
        p: 0.25,
        borderRadius: "10px",
        backgroundColor: color.gray100,
        border: `1px solid ${color.gray200}`,
      }}
    >
      {segments.map((segment) => {
        const selected = segment.value === value;
        return (
          <Box
            key={segment.value}
            component="button"
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(segment.value)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              border: 0,
              borderRadius: "8px",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 12,
              fontWeight: 600,
              color: selected ? color.gray900 : color.gray600,
              backgroundColor: selected ? "white" : "transparent",
              boxShadow: selected ? "0 1px 2px rgba(16, 24, 40, 0.08)" : "none",
              "&:hover": { color: color.gray900 },
              "&:focus-visible": {
                outline: `2px solid ${workspace.primary_dark}`,
                outlineOffset: 1,
              },
            }}
          >
            {segment.tone && (
              <Box
                aria-hidden
                sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: segment.tone }}
              />
            )}
            {segment.label}
            <Box component="span" sx={{ color: color.gray500, fontWeight: 500 }}>
              {segment.count}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export const VoicesStatusTag: React.FC<{ label: string; tone: string }> = ({ label, tone }) => (
  <Box
    component="span"
    sx={{
      px: 0.75,
      py: 0.125,
      borderRadius: "6px",
      fontSize: 11,
      fontWeight: 600,
      lineHeight: "16px",
      whiteSpace: "nowrap",
      color: tone,
      backgroundColor: alpha(tone, 0.1),
    }}
  >
    {label}
  </Box>
);
